import { ExportToCsv, Options } from 'export-to-csv';
import { action, computed, observable } from 'mobx';

import { makePaginated, Paginated, StringKeyMap } from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { makeTableQuery, TableQuery } from '../../adl-gen/common/tabular';
import {
  AppClient,
  ApplicationRole,
  AppUser,
  Competency,
  Quiz,
  Report,
  Skill,
  TrainingOffering
} from '../../adl-gen/totalinteraction/futurefit/db';
import {
  ClientParticipant,
  QuizMapAndScores,
  ResultsScoreSet,
  SkillTrainingOfferingMatrix,
  UsersGroupsMap
} from '../../adl-gen/totalinteraction/futurefit/frontend';
import {
  TempMultiUserQuizResultsRaw,
  UserAttemptHistory,
  UserAttemptHistoryRaw,
  UserQuizAttempt
} from '../../adl-gen/totalinteraction/futurefit/requests';
import { fromPromise, MaybeLoading } from '../../loader/loader';
import { Service } from '../../service/service';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';
import { toSnake } from '../../util/utils';

// tslint:disable-next-line: no-any
// function ignoreUnused(_i: any) { }

// Skill Training Offering matrix
interface STOMatrixRow0 {
  skillId: DbKey<Skill>;
  skillName: string;
  trainingOfferingIds: DbKey<TrainingOffering>[];
}

interface STOMatrixRow1 {
  skillId: DbKey<Skill>;
  skillName: string;
  providerNames: string[];
}

interface STOMatrixRow2 {
  skillId: DbKey<Skill>;
  skillName: string;
  trainingOfferingNames: string[];
}

interface STOMatrixDataRow {
  skillId: DbKey<Skill>;
  skillName: string;
  relevanceScores: string[]; // Actually, number, but we want to input '' for blank cells
}

interface SWRow0 {
  quizId: DbKey<Quiz>;
  quizName: string;
  competencyName: string;
  skillName: string;
  skillId: DbKey<Skill>;
  desired: string;
  userIds: DbKey<AppUser>[]; // Followed by 5 empty cells each
}

interface SWRow1 {
  quizId: DbKey<Quiz>;
  quizName: string;
  competencyName: string;
  skillName: string;
  skillId: DbKey<Skill>;
  desired: string;
  participantNames: string[]; // Followed by 5 empty cells each
}

interface SWRow2 {
  quizId: DbKey<Quiz>;
  quizName: string;
  competencyName: string;
  skillName: string;
  skillId: DbKey<Skill>;
  desired: string;
  userQuizCompletedDates: string[]; // Converted dates - followed by 2 empty cells each
}

interface SWRow3 {
  quizId: DbKey<Quiz>;
  quizNameHeading: string;
  competencyHeading: string;
  skillHeading: string;
  skillIdHeading: string;
  desiredHeading: string;
  scoresAndRatiosHeadings: string[];
}

interface SWDataRow {
  quizId: DbKey<Quiz>;
  quizName: string;
  competencyName: string;
  skillName: string;
  skillId: DbKey<Skill>;
  desired: string; // Actually, number, but we want to input '' for blank cells
  scoresAndRatios: string[]; // Actually, number, but we want to input '' for blank cells and suffix '%' for ratios
}

// Don't need userQuizId if fetching 'previous' results in the datasets object
interface SWUserQuizResults {
  quizId: DbKey<Quiz>;
  datasets: { [key: string]: ResultsScoreSet }; //UserQuizResults['datasets'];
}
interface SWUserResults {
  fullname: string;
  results: SWUserQuizResults[];
}

type SWMergedUserResults = StringKeyMap<DbKey<AppUser>, SWUserResults>;
interface SWSorted<T> {
  name: string;
  id: DbKey<T>;
}

class ReportPortalApi {
  constructor(protected readonly service: Service) {}
  generateParticipantsReport = async (clientId: DbKey<AppClient>): Promise<Paginated<ClientParticipant>> =>
    this.service.queryClientParticipantsReport(clientId);
  generateSkillTrainingOfferingMatrix = async (): Promise<SkillTrainingOfferingMatrix> =>
    this.service.querySkillTrainingOfferingMatrix();
  queryAppClient = async (q: TableQuery): Promise<Paginated<WithDbId<AppClient>>> => this.service.queryAppClients(q);
  queryReports = async (q: TableQuery): Promise<Paginated<WithDbId<Report>>> => this.service.queryReports(q);
  queryQuizReportParticipants = async (viewerId: DbKey<AppUser>, quizId: DbKey<Quiz>): Promise<UsersGroupsMap> =>
    this.service.queryQuizReportParticipants(viewerId, quizId);
  queryQuizReport = async (
    appClientId: DbKey<AppClient>,
    viewerId?: DbKey<AppUser>,
    quizId?: DbKey<Quiz>,
    includedParticipantIds?: string[]
  ): Promise<TempMultiUserQuizResultsRaw> =>
    this.service.queryQuizReportRaw(appClientId, viewerId, quizId, includedParticipantIds);
  queryUserAttemptHistory = async (appClientId: DbKey<AppClient>): Promise<UserAttemptHistoryRaw> =>
    this.service.queryUserAttemptHistory(appClientId);
}

/**
 * Data and actions for reports portal data
 */
export class ReportPortalStore {
  /** Response from the server */
  @observable.ref reports: MaybeLoading<Paginated<WithDbId<Report>>> = {
    kind: 'loading'
  };
  @observable appClient: MaybeLoading<WithDbId<AppClient>> = { kind: 'loading' };

  public static fromService(
    service: Service,
    identity: LoggedIn | LoggedInSuper,
    appClientId?: DbKey<AppClient>
  ): ReportPortalStore {
    const store = new ReportPortalStore(new ReportPortalApi(service), identity, appClientId);
    return store;
  }

  constructor(
    private readonly transport: ReportPortalApi,
    private readonly identity: LoggedIn | LoggedInSuper,
    private readonly appClientId?: DbKey<AppClient>
  ) {
    this.loadAppClient();
    this.load();
  }

  @computed
  public get loading(): MaybeLoading<Paginated<WithDbId<Report>>> {
    return this.reports;
  }

  @computed
  get loadingClient(): MaybeLoading<WithDbId<AppClient>> {
    return this.appClient;
  }

  /** Load reports */
  @action load() {
    this.reports = fromPromise(this.loadForAdmin());
  }

  @action loadAppClient() {
    if (this.appClientId) {
      this.appClient = fromPromise(this.queryAppClient(this.appClientId));
    }
  }

  loadForAdmin = async (): Promise<Paginated<WithDbId<Report>>> => {
    // Only support Client Admin or Super - ignore User
    if (this.identity.kind === 'logged-in' && this.identity.role === ApplicationRole.admin) {
      ///  No need to filter this, it is done on the server
      const reports = await this.transport.queryReports(makeTableQuery({}));
      return reports;
    } else if (this.identity.kind === 'super-logged-in') {
      /// If appClient was specified, get everything, otherwise don't fetch admin or user reports
      /// Can't use TableQuery because the role field is of type ApplicationRole, not text
      const reports = await this.transport.queryReports(makeTableQuery({}));
      if (this.appClientId) {
        return reports;
      } else {
        const items = reports.items.filter(
          rpt => rpt.value.role !== ApplicationRole.admin && rpt.value.role !== ApplicationRole.user
        );
        return makePaginated({ items, current_offset: 0, total_size: items.length });
      }
    } else {
      return Promise.reject('Not authorized');
    }
  };

  queryAppClient = async (id: DbKey<AppClient>): Promise<WithDbId<AppClient>> => {
    const appClient = await this.transport.queryAppClient(
      makeTableQuery({
        filter: { kind: 'equals', value: { field: 'id', value: id } }
      })
    );
    if (appClient.total_size === 1) {
      return appClient.items[0];
    } else {
      return Promise.reject(`Error looking for client ID ${id}`);
    }
  };

  public generateReport = async (reportId: DbKey<Report>): Promise<void> => {
    // TODO: Check if not superuser that clientId === admin client ID (already on server)
    if (this.loading.kind === 'value') {
      const report = this.loading.value.items.find(r => r.id === reportId);
      if (report) {
        switch (report.value.action) {
          case 'ClientParticipant':
            return this.generateParticipantReport(report.value);
          // TODO:                    // case "UserActivity": return this.generateUserActivityReport(clientName, clientId)
          case 'SkillTrainingOfferingMatrix':
            return this.generateSkillTrainingOfferingMatrix(report.value);
          case 'UserResults':
            return this.generateUserResultsReport(report.value);
          case 'AusPostUserHistory':
            return this.generateAusPostUserHistoryReport(report.value);
          default:
            return;
        }
      } else {
        return Promise.reject(`Report not found, id ${reportId}`);
      }
    } else {
      return Promise.reject(`Reports not loaded, status ${this.loading.kind}`);
    }
  };

  generateParticipantReport = async (report: Report): Promise<void> => {
    if (this.loadingClient.kind === 'value') {
      const { id, value: appClient } = this.loadingClient.value;
      const csvExporter = this.getClientCsvExporter(report, appClient.name);
      // Fetch ye data
      const result: Paginated<ClientParticipant> = await this.transport.generateParticipantsReport(id);

      if (result.total_size > 0) {
        /// Should always be at least 1
        csvExporter.generateCsv(result.items);
      } else {
        return Promise.reject('Participant report: no data found');
      }
    } else {
      return Promise.reject(`AppClient not loaded, status ${this.loadingClient.kind}`);
    }
  };
  //     } else {
  //         this.generateEmptyCsv(csvExporter);
  //     }

  generateSkillTrainingOfferingMatrix = async (report: Report): Promise<void> => {
    if (this.identity.kind !== 'super-logged-in') {
      return Promise.reject('Not authorized');
    }

    // Fetch ye data
    const result: SkillTrainingOfferingMatrix = await this.transport.generateSkillTrainingOfferingMatrix();

    /// Now, devolve results into a CSV
    const row0: STOMatrixRow0 = { skillId: '', skillName: '', trainingOfferingIds: [] };
    const row1: STOMatrixRow1 = { skillId: '', skillName: '', providerNames: [] };
    const row2: STOMatrixRow2 = { skillId: '', skillName: 'Skill', trainingOfferingNames: [] };
    for (const [toId, toLookup] of Object.entries(result.offerings)) {
      row0.trainingOfferingIds.push(toId);
      row1.providerNames.push(toLookup.providerName);
      row2.trainingOfferingNames.push(`"${toLookup.name}"`);
    }
    const rows: STOMatrixDataRow[] = [];
    for (const [skillId, skillName] of Object.entries(result.skills)) {
      rows.push({ skillId, skillName, relevanceScores: new Array(row0.trainingOfferingIds.length).fill('') });
    }
    result.values.forEach(entry => {
      const row = rows.find(r => r.skillId === entry.v1.v1);
      if (row) {
        const idx = row0.trainingOfferingIds.findIndex(r => r === entry.v1.v2);
        if (idx >= 0) {
          row.relevanceScores[idx] = `${entry.v2}`;
        }
      }
    });
    /// Now extract the data
    const headers: string[] = [row2.skillName, ...row2.trainingOfferingNames];
    const items: string[][] = [
      // [ row1.skillName, ...row1.providerNames ],
      ...rows.map(row => [row.skillName, ...row.relevanceScores])
    ];

    const csvExporter = this.getCsvExporter(report, headers);
    csvExporter.generateCsv(items);
    // // } else {
    // //     return Promise.reject("Participant report: no data found")
    // // }
  };

  generateUserResultsReport = async (report: Report): Promise<void> => {
    if (this.loadingClient.kind === 'value') {
      const { id: appClientId } = this.loadingClient.value;
      const { name: clientName } = this.loadingClient.value.value;
      // Fetch ye data
      // allResults -> {
      //   results -> MultiUserQuizResults[] {
      //     quizResults -> UserQuizResults[] {
      //       appUserId;
      //       appClientId;
      //       fullname;
      //       userQuizId;
      //       datasets -> Record<'actual' etc -> ResultScoreSet {
      //         title;
      //         overall: Score;
      //         byCompetency: Record<CompetencyId, Score>;
      //         bySkill: Record<SkillId, Score>;
      //       }>
      //      }
      //      quizMapAndScores -> QuizMapAndScores {}
      // }}
      const allResults: TempMultiUserQuizResultsRaw = await this.transport.queryQuizReport(appClientId);

      // Merge and sort items from results
      // [UserQuizResults] -> { [appUserId], { fullname, [ { userQuizId, datasets } ] } } -> sort [ { fullname, appUserId } ]
      // [QuizMapAndScores] doesn't need restructuring but -> sort [ { quiz.title.en, quizId } ]
      const userResults: SWMergedUserResults = {};
      const sortedUsers: SWSorted<AppUser>[] = [];
      const sortedQuizzes: SWSorted<Quiz>[] = [];
      const sortedCompetencies: SWSorted<Competency>[] = [];
      const sortedSkills: SWSorted<Skill>[] = [];
      const quizData: QuizMapAndScores[] = [];
      // Loop over UserQuizResults: appUserId, appClientId, fullname, userQuizId, datasets
      for (const result of allResults.results) {
        // UserQuizResults loop
        for (const quizResult of result.quizResults) {
          // Extract quizId and datasets including 'previous'
          const uqResult: SWUserQuizResults = {
            quizId: result.quizMapAndScores.quiz.id,
            datasets: quizResult.datasets
          };
          // MergedUserResults Record: userId -> SWUserResults { fullname, SWUserQuizResults[] -> { quizId, UserQuizResults['datasets']}}
          if (Object.keys(userResults).includes(quizResult.appUserId)) {
            userResults[quizResult.appUserId].results.push(uqResult);
          } else {
            userResults[quizResult.appUserId] = { fullname: quizResult.fullname, results: [uqResult] };
          }
          // sorted users: to be sorted on name (fullname)
          if (!sortedUsers.find(user => user.id === quizResult.appUserId)) {
            sortedUsers.push({ name: quizResult.fullname, id: quizResult.appUserId });
          }
        }
        // End of results loop, now metadata
        // QuizMapAndScores - ignore other languages for now, use English only and sort on that
        if (!quizData.find(item => item.quiz.id === result.quizMapAndScores.quiz.id)) {
          quizData.push(result.quizMapAndScores);
        }
        // Sorted quizzes: to be sorted on title
        if (!sortedQuizzes.find(q => q.id === result.quizMapAndScores.quiz.id)) {
          sortedQuizzes.push({
            name: result.quizMapAndScores.quiz.value.title.value.en,
            id: result.quizMapAndScores.quiz.id
          });
        }
        // Sorted competencies: to be sorted on name
        for (const competencyId of Object.keys(result.quizMapAndScores.map.competencies)) {
          if (!sortedCompetencies.find(c => c.id === competencyId)) {
            sortedCompetencies.push({
              name: result.quizMapAndScores.map.competencies[competencyId].fullName.value.en,
              id: competencyId
            });
          }
        }
        // Sorted skills: to be sorted on name
        for (const skillId of Object.keys(result.quizMapAndScores.map.skills)) {
          if (!sortedSkills.find(s => s.id === skillId)) {
            sortedSkills.push({ name: result.quizMapAndScores.map.skills[skillId].name.value.en, id: skillId });
          }
        }
      }
      // Sort em
      sortedQuizzes.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      sortedCompetencies.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      sortedSkills.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      // Special sort on names
      sortedUsers.sort((a, b) => this.sortBySurname(a.name.toLowerCase().trim(), b.name.toLowerCase().trim()));

      /// ----- [ CSV format ] --------------------------------------------------------------------------------------
      /// Row 0 not output, Col A not output
      /// Col A       B         C         D          E        F        G                          H         I           J            K         L           M ...
      ///     quizId  QuizName  CompName  SkillName  SkillId  Desired  userId1/Name1/Prev Actual  Prev Gap  Prev Ratio  Curr Actual  Curr Gap  Curr Ratio  userId2/Name2/...
      /// Row 0: [empty] [empty]             [empty]             [empty]         [empty] [empty] userId                             [empty] [empty]  [empty]                           [empty] [empty]  userId2                        [empty] [empty] ...
      /// Row 1: [empty] [empty]             [empty]             [empty]         [empty] [empty] Participant (name sort horizontal) [empty] [empty]  [empty]                           [empty] [empty]  Participant2                   [empty] [empty] ...
      /// Row 2: [empty] [empty]             [empty]             [empty]         [empty] [empty] Prev user quiz completed date      [empty] [empty]  Current user quiz completed date  [empty] [empty]  Prev user quiz completed date  [empty] [empty] ...
      /// Row 3: [empty] Quiz name (alpha)   Competency (alpha)  Skill (alpha)   skillId Desired Actual*                             Gap*    Ratio*  Actual*                           Gap*    Ratio* ... (* = for UserQuizId)
      /// Rows : quizId  Quiz1               Comp1               Skill1          S1      60      xx                                  xx      xx%     xx                                xx      xx%    ...
      /// Now, devolve results into a CSV
      const row0: SWRow0 = {
        quizId: '',
        quizName: '',
        competencyName: '',
        skillName: '',
        skillId: '',
        desired: '',
        userIds: []
      };
      const row1: SWRow1 = {
        quizId: '',
        quizName: '',
        competencyName: '',
        skillName: '',
        skillId: '',
        desired: '',
        participantNames: []
      };
      const row2: SWRow2 = {
        quizId: '',
        quizName: '',
        competencyName: '',
        skillName: '',
        skillId: '',
        desired: '',
        userQuizCompletedDates: []
      };
      const row3: SWRow3 = {
        quizId: '',
        quizNameHeading: 'Quiz',
        competencyHeading: 'Competency',
        skillHeading: 'Skill',
        skillIdHeading: 'Skill Id',
        desiredHeading: 'Desired',
        scoresAndRatiosHeadings: []
      };

      for (const user of sortedUsers) {
        row0.userIds.push(user.id);
        row0.userIds.push('');
        row0.userIds.push('');
        row0.userIds.push('');
        row0.userIds.push('');
        row0.userIds.push('');
        row1.participantNames.push(user.name);
        row1.participantNames.push('');
        row1.participantNames.push('');
        row1.participantNames.push('');
        row1.participantNames.push('');
        row1.participantNames.push('');
        // Currently we only get the UserQuizId for the *current* UserQuiz, 
        // so the dates for both 'current' and 'previous' results are unavailable
        row2.userQuizCompletedDates.push('Previous Results');
        row2.userQuizCompletedDates.push('');
        row2.userQuizCompletedDates.push('');
        row2.userQuizCompletedDates.push('Current Results');
        row2.userQuizCompletedDates.push('');
        row2.userQuizCompletedDates.push('');
        row3.scoresAndRatiosHeadings.push('Actual');
        row3.scoresAndRatiosHeadings.push('Gap');
        row3.scoresAndRatiosHeadings.push('Ratio %');
        row3.scoresAndRatiosHeadings.push('Actual');
        row3.scoresAndRatiosHeadings.push('Gap');
        row3.scoresAndRatiosHeadings.push('Ratio %');
      }

      const rows: SWDataRow[] = [];

      for (const quiz of sortedQuizzes) {
        const qms = quizData.find(data => data.quiz.id === quiz.id);
        if (qms) {
          for (const competency of sortedCompetencies) {
            if (Object.keys(qms.map.competencies).includes(competency.id)) {
              for (const skill of sortedSkills) {
                if (qms.map.skillsByCompetency[competency.id].includes(skill.id)) {
                  rows.push({
                    quizId: quiz.id,
                    quizName: quiz.name,
                    competencyName: competency.name,
                    skillName: skill.name,
                    skillId: skill.id,
                    // tslint:disable-next-line: no-string-literal
                    desired: `${qms.datasets['expected'].bySkill[skill.id]}`,
                    scoresAndRatios: new Array(sortedUsers.length * 6).fill('')
                  });
                }
              }
            }
          }
        }
      }

      // quiz, comp, skill present; look for users...
      for (const [key, val] of Object.entries(userResults)) {
        // Look up results userId in Row 0, which has the (sorted) userIds at the correct column offsets
        const col = row0.userIds.findIndex(item => item === key);
        if (col >= 0) {
          for (const result of val.results) {
            // Assumption: 'actual' and 'difference' datasets will always exist
            const actualResults = result.datasets['actual'];
            const previousResults = result.datasets['previous'];
            const differences = result.datasets['difference'];
            for (const skillId of Object.keys(actualResults.bySkill)) {
              // The row offsets are set on quiz/skill ID
              const row = rows.find(r => r.quizId === result.quizId && r.skillId === skillId);
              if (row) {
                const desired = +row.desired;
                // If previous results exist at all, we should expect the BySkill values to exist
                // Results may be zero - 'Not part of my role'
                if (previousResults) {
                  const previous = +previousResults.bySkill[skillId];
                  const prevDifference = previous - desired;
                  const prevRatio = desired === 0 ? '' : Math.floor((previous / desired) * 100)
                  // Previous - Gap - Ratio
                  row.scoresAndRatios[col] = `${previous}`;
                  row.scoresAndRatios[col + 1] = `${prevDifference}`;
                  row.scoresAndRatios[col + 2] = `${prevRatio}%`;
                }
                const actual = actualResults.bySkill[skillId];
                const difference = differences.bySkill[skillId];
                const ratio = desired === 0 ? '' : Math.floor((actual / desired) * 100);
                // Actual - Gap - Ratio
                row.scoresAndRatios[col + 3] = `${actual}`;
                row.scoresAndRatios[col + 4] = `${difference}`;
                row.scoresAndRatios[col + 5] = `${ratio}%`;
              }
            }
          }
        }
      }
      /// Now extract the data
      const headers: string[] = [
        row1.quizName,
        row1.competencyName,
        row1.skillName,
        row1.skillId,
        row1.desired,
        ...row1.participantNames
      ];
      const subheaders: string[] = [
        row2.quizName,
        row2.competencyName,
        row2.skillName,
        row2.skillId,
        row2.desired,
        ...row2.userQuizCompletedDates
      ];
      const items: string[][] = [
        subheaders,
        [
          row3.quizNameHeading,
          row3.competencyHeading,
          row3.skillHeading,
          row3.skillIdHeading,
          row3.desiredHeading,
          ...row3.scoresAndRatiosHeadings
        ],
        ...rows.map(row => [
          row.quizName,
          row.competencyName,
          row.skillName,
          row.skillId,
          row.desired,
          ...row.scoresAndRatios
        ])
      ];

      const csvExporter = this.getClientCsvExporter(report, clientName, headers);
      csvExporter.generateCsv(items);
    }
  };

  /**
   * AusPost User Report: one row per participant per assessment attempt (every
   * attempt, not just current+previous), skills listed alphabetically, with
   * the participant's current manager.
   */
  generateAusPostUserHistoryReport = async (report: Report): Promise<void> => {
    if (this.loadingClient.kind === 'value') {
      const { id: appClientId } = this.loadingClient.value;
      const { name: clientName } = this.loadingClient.value.value;

      const raw: UserAttemptHistoryRaw = await this.transport.queryUserAttemptHistory(appClientId);

      // Quiz metadata (title, skill maps, "expected"/desired scores), keyed by quizId
      const quizDataByQuizId: { [quizId: string]: QuizMapAndScores } = {};
      for (const qms of raw.quizData) {
        quizDataByQuizId[qms.quiz.id] = qms;
      }

      // Alphabetical union of every skill referenced across all of this client's quizzes
      const sortedSkills: SWSorted<Skill>[] = [];
      for (const qms of raw.quizData) {
        for (const skillId of Object.keys(qms.map.skills)) {
          if (!sortedSkills.find(s => s.id === skillId)) {
            sortedSkills.push({ name: qms.map.skills[skillId].name.value.en, id: skillId });
          }
        }
      }
      sortedSkills.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

      const headers: string[] = ['Participant', 'Manager', 'Assessment', 'Attempt Date'];
      for (const skill of sortedSkills) {
        headers.push(`${skill.name} - Desired`);
        headers.push(`${skill.name} - Actual`);
        headers.push(`${skill.name} - Gap`);
        headers.push(`${skill.name} - Ratio %`);
      }

      const sortedResults: UserAttemptHistory[] = [...raw.results].sort((a, b) =>
        this.sortBySurname(a.fullname.toLowerCase().trim(), b.fullname.toLowerCase().trim())
      );

      const items: string[][] = [];
      for (const user of sortedResults) {
        // Most recent attempt first
        const sortedAttempts: UserQuizAttempt[] = [...user.attempts].sort((a, b) => b.completedAt - a.completedAt);
        for (const attempt of sortedAttempts) {
          const qms = quizDataByQuizId[attempt.quizId];
          const row: string[] = [
            user.fullname,
            user.managerFullname || '',
            qms ? qms.quiz.value.title.value.en : '',
            new Date(attempt.completedAt).toISOString().slice(0, 10)
          ];
          const expected = qms && qms.datasets.expected ? qms.datasets.expected.bySkill : {};
          for (const skill of sortedSkills) {
            const desired = expected[skill.id];
            const actual = attempt.result.bySkill[skill.id];
            if (desired === undefined || actual === undefined) {
              row.push('', '', '', '');
            } else {
              const gap = actual - desired;
              const ratio = desired === 0 ? '' : Math.floor((actual / desired) * 100);
              row.push(`${desired}`, `${actual}`, `${gap}`, `${ratio}%`);
            }
          }
          items.push(row);
        }
      }

      if (items.length === 0) {
        // export-to-csv throws on an empty dataset (Object.keys(this._data[0]))
        // rather than producing an empty file, so guard against it explicitly.
        return Promise.reject({ publicMessage: 'No completed assessments found to report on for this client yet.' });
      }

      const csvExporter = this.getClientCsvExporter(report, clientName, headers);
      csvExporter.generateCsv(items);
    }
  };

  // Sort by surname and forename - allow for 2 surnames
  sortBySurname = (a: string, b: string) =>
    a
      .split(' ', 3)
      .reverse()
      .join(' ')
      .localeCompare(
        b
          .split(' ', 3)
          .reverse()
          .join(' ')
      );

  getClientCsvExporter = (report: Report, clientName: string, headers?: string[]): ExportToCsv => {
    const options = this.getCsvExporterOptions(
      toSnake(`${report.title.toLowerCase()}-${clientName.toLowerCase()}`),
      `${report.title}-${clientName}`,
      headers
    );
    return new ExportToCsv(options);
  };

  getCsvExporter = (report: Report, headers?: string[]): ExportToCsv => {
    const options = this.getCsvExporterOptions(toSnake(report.title.toLowerCase()), report.title, headers);
    return new ExportToCsv(options);
  };

  getCsvExporterOptions = (filename: string, title: string, headers?: string[]): Options => {
    const options: Options = {
      filename,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: true,
      title,
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: headers === undefined,
      headers
    };
    return options;
  };

  //   generateEmptyCsv = (exporter: ExportToCsv): void => {
  //     (keyof ClientParticipant).
  //     const emptyItem: ClientParticipant = makeClientParticipant({
  //         userId: "",
  //         firstname: "",
  //         lastname: "",
  //         email: "",
  //         isManager: "",
  //         teamName: "",
  //         status: "",
  //         quizId: "",
  //         quizTitle: "",
  //         attempt: 0,
  //         completed: "",
  //         lastModified: "",
  //         totalTime: ""
  //       });
  //       exporter.generateCsv([emptyItem]);
  //   }
}



// WEBPACK FOOTER //
// ./src/admin/stores/report-portal-store.ts